import axios from "axios";
import {
    ACCESS_TOKEN_LOCAL_STORAGE_KEY,
    CONFIG_STORAGE_KEY,
    REFRESH_TOKEN_LOCAL_STORAGE_KEY
} from "core/common/constants/localstorage";
import { navigate, usePath } from "raviger";
import { isExpired } from "react-jwt";

import { API_ENDPOINT } from "core/common/constants/env";
import * as Utils from "core/common/utils";
import { setReturnUrl } from "core/route/index";

let REFRESHING = false;
let AUTO_REFRESHING_TIMER = -1;

const REFRESH_TOKEN_URL = "/connect/refresh_token";

// helps to start refreshing before RT expiration by some seconds
const RETRACT_REFRESH_MILISECCONDS = 10000; // 10 seconds

const loginUrls = [
    "/connect/token",
    "/connect/otp/generate",
    "/connect/otp",
    "/connect/password/forget",
    "/connect/password/forgetactivation"
];

const inSecureUrls = ["/connect", "/meta"];

const apiNotRequiresToken = (url) =>
    inSecureUrls.filter((a) => url.startsWith(a)).length > 0;

export const navigateLogin = () => {
    removeToken();

    //localStorage.removeItem(GRECAPTCHA_STORAGE_KEY);
    //localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    //localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
    //localStorage.removeItem(SUBBUSINESS_ID_LOCAL_STORAGE_KEY);
    //localStorage.removeItem(CONFIG_STORAGE_KEY);

    setReturnUrl(usePath);

    return navigate("/login");
};

const startRefrshTokenTimer = () => {
    let exp_ms = Utils.JWT().exp_ms;
    exp_ms -= RETRACT_REFRESH_MILISECCONDS;

    console.debug(`Now: ${new Date().toString()}. Refresh in ${exp_ms / 1000}s`);

    AUTO_REFRESHING_TIMER = setTimeout(async () => {
        console.debug("RT. Refresh it now...");

        stopRefreshingTimer();
    }, exp_ms);
};

//#region HTTP Client instence interseptors

//#region Request suspender
//https://stackoverflow.com/questions/7307983/while-variable-is-not-defined-wait
const delay = (timer) => {
    return new Promise((resolve) => {
        timer = timer || 500;

        setTimeout(resolve, timer);
    });
};

//And define what ever you want with async fuction

const suspend = async () => {
    while (REFRESHING) {
        await delay(500);
    }
};

//#endregion

const client = axios.create({
    baseURL: `${API_ENDPOINT()}/api`,
    timeout: 20 * 60 * 1000 // in development only!! in prod it should be set to 2500 - 5000
});

const prosessResponseSuccess = (response) => {


    const { config, data: responseData } = response || {};
    const { data } = responseData;
    const { url } = config || {};
    if (data) {
        const { access_token, refresh_token, config: cfg } = data;

        if (cfg) {
            // a login response has the full config (including the permission flags), so it replaces the saved one.
            // other responses (e.g. the token refresh) may not have the flags, so they are merged and can't erase them
            const savedCfg = loginUrls.includes(url)
                ? {}
                : JSON.parse(localStorage.getItem(CONFIG_STORAGE_KEY) || "{}");
            localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({ ...savedCfg, ...cfg }));
        }

        const login =
            loginUrls.includes(url) &&
            Utils.isSuccessfulRequest(null, responseData, false) &&
            access_token;

        if (login) {
            sessionStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, access_token);
            localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY, refresh_token);

            startRefrshTokenTimer();
        }
    }

    console.debug("api OK:", url);

    return response;
};

const letHandleErrorOutsideRegex = [
    // /^\/business\/\d+\d*$/,
];

const shouldAllowHandleErrorOutside = (url) =>
    loginUrls.includes(url) ||
    letHandleErrorOutsideRegex.some((x) => x.test(url));

const prosessResponseError = async (error) => {
    const { config } = error;
    const originalRequest = config;
    if (config) {
        const { url } = config;
        if (url === REFRESH_TOKEN_URL) {
            return Promise.reject(error);
        }
        const { response } = error;
        const { status } = response || {}; // in case of CORS the response is undefined
        if (!shouldAllowHandleErrorOutside(url) && status === 401) {
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);

            if (!refreshToken) {
                forceLogout();
                return Promise.resolve(response);
            }

            const token = sessionStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);

            const expired = isExpired(token);

            if ((token && expired) || !token) {
                if (!isRefreshing) {
                    isRefreshing = true;
                    refreshPromise = tryRefreshToken().finally(() => {
                        isRefreshing = false;
                    });
                }

                await refreshPromise;

                const newToken = sessionStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
                originalRequest.headers = {
                    ...originalRequest.headers,
                    Authorization: `Bearer ${newToken}`,
                    Accept: "application/json",
                    "Content-Type": "application/json"
                };
                return client(originalRequest);

            } else {
                // if we got here, it means either the token or refresh token aren't valid for some reason.
                forceLogout();
                return Promise.resolve(response);
            }

        }

        //if (status === 401) {
        //    forceLogout();
        //}

        return Promise.resolve(response);
    }

    return Promise.reject(error);
};

let isRefreshing = false;
let refreshPromise = null;
client.interceptors.request.use(
    async (config) => {

        if (apiNotRequiresToken(config.url)) {
            return Promise.resolve(config);
        }

        console.debug(`api: the first request:`, config.url);


        let access_token = sessionStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY) ?? "Empty Token For First Request Without Real Token";


        if (!access_token) {

            const cancelToken = axios.CancelToken;

            return {
                ...config,
                cancelToken: new cancelToken((cancel) =>
                    cancel(`Cancel unauthorized request: ${config.url}`)
                )
            };
        } else {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${access_token}`,
                Accept: "application/json",
                "Content-Type": "application/json"
            };

            return Promise.resolve(config);
        }
    },
    (error) => Promise.reject(error)
);

client.interceptors.response.use(prosessResponseSuccess, prosessResponseError);

export const tryRefreshToken = async (isInitialLoad = false) => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
    const token = sessionStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);

    if (!refreshToken) {
        console.log("Refresh Token not found");
        return;
    }


    if (token && !isExpired(token)) {
        return;
    }

    try {
        const response = await refreshAccessToken(refreshToken);
        const { data: responseData } = response;
        const { code, data } = responseData;

        // code 0 indicate that the response is ok
        // in this case we need to refresh the local storage with the new reresh token and access token
        if (code === 0) {
            const { access_token, refresh_token } = data;
            sessionStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, access_token);
            localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY, refresh_token);
            if (isInitialLoad) {
                window.location.reload();
            }
            return;
        }

        // if code is not equal to 0 it means that the refresh operation has been failed
        // in this case we need to redirect the user to the login page
        throw new Error("No tokens");

    } catch (error) {
        //remove refresh token to make the site redirect to logout page
        console.log("Failed to refresh token � attempting to remove it.");
        forceLogout();
    }

};

const forceLogout = () => {
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
    window.location.reload();
}

const refreshAccessToken = async (refreshToken) => {
    return client.post(REFRESH_TOKEN_URL, {
        refresh_token: refreshToken
    });
};

const stopRefreshingTimer = () => {
    clearTimeout(AUTO_REFRESHING_TIMER);

    AUTO_REFRESHING_TIMER = 0;
};

export const removeToken = () => {
    delete axios.defaults.headers.common.Authorization;
};

export default client;

//#endregion
